import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { savePermitForm, updatePermitForm } from '../../store/slices/projectPermitsSlice';
import { showToast } from '../utils/Toast';
import { fetchProjectData, prefillFormData } from '../../utils/projectDataUtils';
import { printPermitForm, createCompanyHeader, formatFormField, formatTableRow } from '../../utils/PermitPrintUtils';
import FloatingActionBar from './FloatingActionBar';

const PermitRequestForm = ({ projectId, currentPermit, onClose }) => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    date: '',
    jobNumber: '',
    propertyOwner: '',
    ownerPhone: '',
    ownerEmail: '',
    jobLocation: '',
    inHomeownersAssociation: '',
    inMobileHomePark: '',
    roofTypeRemoved: '',
    roofWeightRemoved: '',
    newRoofType: '',
    newRoofMaterial: '',
    newMaterialWeight: '',
    roofMaterialColor: '',
    esrNumber: '',
    roofingSquares: '',
    roofSlope: '',
    projectValuation: '',
    additionalNotes: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (currentPermit && currentPermit.formType === 'permitRequestForm') {
      setFormData(currentPermit.formData || formData);
    } else {
      // Fetch project data to prefill the form
      const getProjectData = async () => {
        try {
          // Get project data
          const projectData = await fetchProjectData(projectId);
          console.log('Project data for permit request form:', projectData);
          
          // Handle the case where the project is nested in a 'project' property
          const actualProject = projectData.project || projectData;
          
          // Prefill the form data
          const prefilled = prefillFormData(actualProject, formData);
          
          // Additional prefilling specific to this form
          if (actualProject.client) {
            prefilled.propertyOwner = actualProject.client.name || '';
            prefilled.ownerPhone = actualProject.client.cellPhone || '';
            prefilled.ownerEmail = actualProject.client.email || '';
          }
          
          // Set today's date in MM/DD/YYYY format
          const now = new Date();
          const month = String(now.getMonth() + 1).padStart(2, '0');
          const day = String(now.getDate()).padStart(2, '0');
          const year = now.getFullYear();
          prefilled.date = `${month}/${day}/${year}`;
          
          // Set job number
          if (actualProject.jobNumber) {
            prefilled.jobNumber = actualProject.jobNumber;
          } else if (actualProject.originalLead && actualProject.originalLead.jobNumber) {
            prefilled.jobNumber = actualProject.originalLead.jobNumber;
          }
          
          // Get full address with city
          if (actualProject.address) {
            const addressParts = [];
            if (actualProject.address.street) addressParts.push(actualProject.address.street);
            if (actualProject.address.city) addressParts.push(actualProject.address.city);
            if (actualProject.address.state) addressParts.push(actualProject.address.state);
            if (actualProject.address.zipCode) addressParts.push(actualProject.address.zipCode);
            
            prefilled.jobLocation = addressParts.join(', ');
          } else if (actualProject.siteContact && actualProject.siteContact.address) {
            const addressParts = [];
            if (actualProject.siteContact.address.street) addressParts.push(actualProject.siteContact.address.street);
            if (actualProject.siteContact.address.city) addressParts.push(actualProject.siteContact.address.city);
            if (actualProject.siteContact.address.state) addressParts.push(actualProject.siteContact.address.state);
            if (actualProject.siteContact.address.zipCode) addressParts.push(actualProject.siteContact.address.zipCode);
            
            prefilled.jobLocation = addressParts.join(', ');
          }
          
          console.log('Prefilled permit request form data:', prefilled);
          setFormData(prefilled);
        } catch (error) {
          console.error('Error prefilling permit request form data:', error);
        }
      };
      
      getProjectData();
    }
  }, [currentPermit, projectId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (currentPermit && currentPermit._id) {
        await dispatch(updatePermitForm({
          projectId,
          permitId: currentPermit._id,
          formData: {
            ...currentPermit,
            formData
          }
        })).unwrap();
        showToast('Permit Request Form updated successfully', 'success');
      } else {
        await dispatch(savePermitForm({
          projectId,
          formType: 'permitRequestForm',
          formData
        })).unwrap();
        showToast('Permit Request Form saved successfully', 'success');
      }
      onClose();
    } catch (error) {
      showToast(error.message || 'Failed to save Permit Request Form', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  const handleDownload = () => {
    // Create a printable version of the form content
    const formContent = `
      ${createCompanyHeader()}
      <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 20px;">
        <h1 class="form-title">CITY BUILDING PERMIT REQUEST FORM</h1>
        <div style="width: 200px;">
          ${formatFormField('Date', formData.date)}
          ${formatFormField('JOB NUMBER', formData.jobNumber)}
        </div>
      </div>
      
      <div class="form-section">
        ${formatFormField('Property Owner', formData.propertyOwner)}
        ${formatFormField('Phone', formData.ownerPhone)}
        ${formatFormField('Email', formData.ownerEmail)}
        ${formatFormField('Job Location', formData.jobLocation)}
      </div>
      
      <h2 class="form-subtitle">PROJECT DETAILS</h2>
      
      <table>
        <tbody>
          ${formatTableRow('Is this in a homeowners association?', formData.inHomeownersAssociation)}
          ${formatTableRow('Is this in a mobile home park?', formData.inMobileHomePark)}
          ${formatTableRow('What type of roof is being removed?', formData.roofTypeRemoved)}
          ${formatTableRow('What is the approx weight of the roof being removed?', formData.roofWeightRemoved)}
          ${formatTableRow('What type of new roof is being installed?', formData.newRoofType)}
          ${formatTableRow('What is the new roof material and manufacturer?', formData.newRoofMaterial)}
          ${formatTableRow('What is the weight of the new material being installed?', formData.newMaterialWeight)}
          ${formatTableRow('What is the roof material color?', formData.roofMaterialColor)}
          ${formatTableRow('What is the ESR# for new material being installed?', formData.esrNumber)}
          ${formatTableRow('How many roofing squares?', formData.roofingSquares)}
          ${formatTableRow('Roof slope?', formData.roofSlope)}
          ${formatTableRow('Project Valuation (Our cost for the project)', formData.projectValuation)}
        </tbody>
      </table>
      
      <div class="form-section">
        <div class="form-label">Additional Notes:</div>
        <div class="form-value" style="white-space: pre-wrap;">${formData.additionalNotes || ''}</div>
      </div>
    `;
    
    // Print the form
    printPermitForm(formData, 'City Building Permit Request', formContent);
  };

  return (
    <div className="permit-form-shell bg-white p-6 rounded-lg max-w-5xl mx-auto relative">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <img 
            src="/logo/JRT-logo-small.jpg" 
            alt="JRT Roofing & Waterproofing" 
            className="h-16 mr-4"
          />
          <div>
            <h2 className="text-2xl font-bold">JRT ROOFING & WATERPROOFING</h2>
            <p className="text-sm">QLTY CRAFTSMANSHIP</p>
          </div>
        </div>
        <div className="text-right">
          <p>P.O. Box 3688</p>
          <p>San Clemente, CA 92672</p>
          <p>Phone: (949) 742-2892</p>
          <p className="uppercase">JRTROOFING.COM</p>
          <p>Lic# C-39 1070037</p>
        </div>
      </div>

      <div className="flex justify-between items-start mb-6">
        <h1 className="text-2xl font-bold">CITY BUILDING PERMIT REQUEST FORM</h1>
        <div className="flex flex-col space-y-2 w-64">
          <div className="flex items-center justify-between">
            <label className="font-medium">Date:</label>
            <input
              type="text"
              name="date"
              value={formData.date}
              onChange={handleChange}
              className="w-40 p-1 border border-gray-300"
            />
          </div>
          <div className="flex items-center justify-between">
            <label className="font-medium">JOB NUMBER:</label>
            <input
              type="text"
              name="jobNumber"
              value={formData.jobNumber}
              onChange={handleChange}
              className="w-40 p-1 border border-gray-300"
            />
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium mb-1">Property Owner:</label>
            <input
              type="text"
              name="propertyOwner"
              value={formData.propertyOwner}
              onChange={handleChange}
              className="w-full p-2 border border-gray-300"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Phone:</label>
            <input
              type="text"
              name="ownerPhone"
              value={formData.ownerPhone}
              onChange={handleChange}
              className="w-full p-2 border border-gray-300"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Email:</label>
            <input
              type="email"
              name="ownerEmail"
              value={formData.ownerEmail}
              onChange={handleChange}
              className="w-full p-2 border border-gray-300"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Job location:</label>
            <input
              type="text"
              name="jobLocation"
              value={formData.jobLocation}
              onChange={handleChange}
              className="w-full p-2 border border-gray-300"
            />
          </div>
        </div>

        <h2 className="text-xl font-bold mb-4">PROJECT DETAILS</h2>
        
        <div className="overflow-x-auto mb-6">
          <table className="w-full border-collapse border border-gray-300">
            <tbody>
              <tr>
                <td className="border border-gray-300 p-2">Is this in a homeowners association?</td>
                <td className="border border-gray-300 p-2">
                  <input
                    type="text"
                    name="inHomeownersAssociation"
                    value={formData.inHomeownersAssociation}
                    onChange={handleChange}
                    className="w-full p-1 border border-gray-300"
                  />
                </td>
              </tr>
              <tr>
                <td className="border border-gray-300 p-2">Is this in a mobile home park?</td>
                <td className="border border-gray-300 p-2">
                  <input
                    type="text"
                    name="inMobileHomePark"
                    value={formData.inMobileHomePark}
                    onChange={handleChange}
                    className="w-full p-1 border border-gray-300"
                  />
                </td>
              </tr>
              <tr>
                <td className="border border-gray-300 p-2">What type of roof is being removed?</td>
                <td className="border border-gray-300 p-2">
                  <input
                    type="text"
                    name="roofTypeRemoved"
                    value={formData.roofTypeRemoved}
                    onChange={handleChange}
                    className="w-full p-1 border border-gray-300"
                  />
                </td>
              </tr>
              <tr>
                <td className="border border-gray-300 p-2">What is the approx weight of the roof being removed?</td>
                <td className="border border-gray-300 p-2">
                  <input
                    type="text"
                    name="roofWeightRemoved"
                    value={formData.roofWeightRemoved}
                    onChange={handleChange}
                    className="w-full p-1 border border-gray-300"
                  />
                </td>
              </tr>
              <tr>
                <td className="border border-gray-300 p-2">What type of new roof is being installed?</td>
                <td className="border border-gray-300 p-2">
                  <input
                    type="text"
                    name="newRoofType"
                    value={formData.newRoofType}
                    onChange={handleChange}
                    className="w-full p-1 border border-gray-300"
                  />
                </td>
              </tr>
              <tr>
                <td className="border border-gray-300 p-2">What is the new roof material and manufacturer?</td>
                <td className="border border-gray-300 p-2">
                  <input
                    type="text"
                    name="newRoofMaterial"
                    value={formData.newRoofMaterial}
                    onChange={handleChange}
                    className="w-full p-1 border border-gray-300"
                  />
                </td>
              </tr>
              <tr>
                <td className="border border-gray-300 p-2">What is the weight of the new material being installed?</td>
                <td className="border border-gray-300 p-2">
                  <input
                    type="text"
                    name="newMaterialWeight"
                    value={formData.newMaterialWeight}
                    onChange={handleChange}
                    className="w-full p-1 border border-gray-300"
                  />
                </td>
              </tr>
              <tr>
                <td className="border border-gray-300 p-2">What is the roof material color?</td>
                <td className="border border-gray-300 p-2">
                  <input
                    type="text"
                    name="roofMaterialColor"
                    value={formData.roofMaterialColor}
                    onChange={handleChange}
                    className="w-full p-1 border border-gray-300"
                  />
                </td>
              </tr>
              <tr>
                <td className="border border-gray-300 p-2">What is the ESR# for new material being installed?</td>
                <td className="border border-gray-300 p-2">
                  <input
                    type="text"
                    name="esrNumber"
                    value={formData.esrNumber}
                    onChange={handleChange}
                    className="w-full p-1 border border-gray-300"
                  />
                </td>
              </tr>
              <tr>
                <td className="border border-gray-300 p-2">How many roofing squares?</td>
                <td className="border border-gray-300 p-2">
                  <input
                    type="text"
                    name="roofingSquares"
                    value={formData.roofingSquares}
                    onChange={handleChange}
                    className="w-full p-1 border border-gray-300"
                  />
                </td>
              </tr>
              <tr>
                <td className="border border-gray-300 p-2">Roof slope?</td>
                <td className="border border-gray-300 p-2">
                  <input
                    type="text"
                    name="roofSlope"
                    value={formData.roofSlope}
                    onChange={handleChange}
                    className="w-full p-1 border border-gray-300"
                  />
                </td>
              </tr>
              <tr>
                <td className="border border-gray-300 p-2">Project Valuation (Our cost for the project)</td>
                <td className="border border-gray-300 p-2">
                  <input
                    type="text"
                    name="projectValuation"
                    value={formData.projectValuation}
                    onChange={handleChange}
                    className="w-full p-1 border border-gray-300"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium mb-1">Additional Notes:</label>
          <textarea
            name="additionalNotes"
            value={formData.additionalNotes}
            onChange={handleChange}
            rows={5}
            className="w-full p-2 border border-gray-300"
          ></textarea>
        </div>

        {/* Add padding at the bottom to ensure content isn't hidden behind the floating bar */}
        <div className="pb-20"></div>
      </form>
      
      {/* Floating Action Bar */}
      <FloatingActionBar 
        onCancel={onClose}
        onDownload={handleDownload}
        onSubmit={handleSubmit}
        loading={loading}
        isUpdate={!!(currentPermit && currentPermit._id)}
      />
    </div>
  );
};

export default PermitRequestForm;
